import React, { useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch, useSelector } from "react-redux";

import { getProductSubscribed } from "../../src/actionCreators/postAuthActionCreators";
import { requestUserProfile, resetUserCache } from "../../src/actionCreators/userProfileActionCreators";
import { requestSessionIdleMinutes } from "../../src/actions/authActions";
import FeatureDisabled from "../../src/components/Common/Navigation/FeatureDisabled";
import LoadingScreen from "../../src/components/Common/Navigation/LoadingScreen";
import SessionExpireModal from "../../src/components/SessionExpireModal";
import SessionTimeout from "../../src/components/SessionTimeout";
import { PageDisabledTypes } from "../../src/Core/ViewModels/User/UserViewModel";
import { IsExchangeUser, getMiliSecondsFromMinutes, getCookie } from "../../src/helper/HelperFunctions";
import { ILogger } from "../../src/Logger/Logger";
import { TelemetryLogger } from "../../src/Logger/TelemetryLogger";
import { getAccessToken } from "../../src/oidcClient/userService";
import { actionTypes } from "../../src/types/postAuthTypes";
import { ApplicationState } from "../store";
import UserAutoLogoutModal from "../../src/components/Common/Modal/UserAutoLogoutModal";
import SignalRWebSocket from "../../src/components/signalR/SignalRWebSocketContainer";
import { Notification } from "../../src/components/Common/Notification/Notification";
import { getRestrictedFileTypes } from "../../src/actionCreators/restrictedFileTypeActionCreators";
import { fetchFileTypeSettings } from "src/actionCreators/adminSettingsActionCreators";

const WarningVisibleMinutes = 1;
export let logger: ILogger;

const LoggedIn: React.FC = props => {
    let sessionExpired;
    const userAuth = useSelector((state: ApplicationState) => state.userAuth);
    const user = useSelector((state: ApplicationState) => state.userProfile);
    const { userAutoLogoutCause, showAutoLogoutModal } = useSelector((state: ApplicationState) => state.signalRState);
    const [timeout, setTimeoutMinutes] = useState(getMiliSecondsFromMinutes(20 - WarningVisibleMinutes));
    const [showSessionTimeout, toggleSessionTimeout] = useState(false);
    const [showSessionExpired, toggleSessionExpired] = useState(false);
    const [productPermission, setProductPermission] = useState<boolean>(false);
    const handleOnIdle = () => toggleSessionTimeout(true);
    const dispatch = useDispatch();
    const { exchangeEnabled, productEnabled, productSubscribed, postAuthLoading, postAuthCompleted } = useSelector(
        (state: ApplicationState) => state.postAuthState
    );

    useEffect(() => {
        if (userAuth.user) {
            dispatch(requestUserProfile());
            dispatch(getProductSubscribed());
            getDefaultSessionIdleTime();
            dispatch(getRestrictedFileTypes());
            dispatch(fetchFileTypeSettings());
            setIntervalSessionExpiredCheck();
            logger = TelemetryLogger.getInstance();
            if (getCookie("userAutoLogout") !== "1") {

                dispatch(resetUserCache());
            }
        }

        return () => {
            clearInterval(sessionExpired);
        };
    }, [userAuth.user]);

    useEffect(() => {
        const hasPermission = IsExchangeUser(userAuth.user);
        setProductPermission(hasPermission);
    }, [user?.userId]);

    useEffect(() => {
        const exchangeEnabled = productSubscribed && productEnabled && productPermission;
        dispatch({ type: actionTypes.SET_EXCHANGE_ENABLE_STATUS, payload: exchangeEnabled });
    }, [productEnabled, productSubscribed, productPermission]);

    const getPageDisableType = () => {
        if (!productSubscribed) {
            return PageDisabledTypes.SubscriptionTier;
        } else if (!productEnabled) {
            return PageDisabledTypes.ProductDisabled;
        } else if (!productPermission) {
            return PageDisabledTypes.NoPermission;
        } else {
            return 0;
        }
    };

    const { reset, getRemainingTime } = useIdleTimer({ timeout, onIdle: handleOnIdle });

    const getDefaultSessionIdleTime = () => {
        dispatch(
            requestSessionIdleMinutes(data =>
                setTimeoutMinutes(getMiliSecondsFromMinutes(parseInt(data.toString()) - WarningVisibleMinutes))
            )
        );
    };

    const handleOnStayAlive = (e: React.SyntheticEvent<EventTarget>) => {
        e?.preventDefault();
        reset();
        toggleSessionTimeout(false);
    };

    const setIntervalSessionExpiredCheck = () => {
        sessionExpired = setInterval(() => {
            if (!getAccessToken()) {
                sessionStorage.removeItem("isVisited");
                toggleSessionExpired(true);
            }
        }, 1000);
    };
    return !userAuth.isLoadingUser && userAuth.user && !postAuthLoading && !!user?.userId ? (
        <>
            {exchangeEnabled ? (
                <>
                    {props.children}
                    <SessionExpireModal show={showSessionExpired} />
                    <SessionTimeout
                        showModal={showSessionTimeout}
                        onStayAlive={handleOnStayAlive}
                        countDownMinutes={WarningVisibleMinutes}
                    />
                </>
            ) : (
                <FeatureDisabled type={getPageDisableType()} />
            )}
            <SignalRWebSocket />
            <Notification />
            <UserAutoLogoutModal logoutCause={userAutoLogoutCause} show={showAutoLogoutModal} />
        </>
    ) : (
        userAuth.signInPageLoaded ? <></> :
            <LoadingScreen />
    );
};

LoggedIn.displayName = "LoggedIn";

export default LoggedIn;
